import { useEffect, useMemo, useState } from "react";
import { Calculator, ChevronDown, GraduationCap } from "lucide-react";

const N = (v) => { const n = parseFloat(String(v).replace(/[^0-9.\-]/g, "")); return isNaN(n) ? 0 : n; };
const $ = (v) => (v < 0 ? "-$" : "$") + Math.abs(Math.round(v)).toLocaleString();
const pct = (v) => (v * 100).toFixed(1) + "%";
const IN = "w-full bg-navy border border-white/20 text-white px-2.5 py-1.5 text-sm focus:border-gold focus:outline-none";
const LB = "block text-[11px] text-gray-400 mb-1";

const F = (label, key, val, set, suffix) => (
  <div><label className={LB}>{label}{suffix ? ` (${suffix})` : ""}</label>
    <input className={IN} value={val} onChange={(e) => set(key, e.target.value)} data-testid={`asm-${key}`} inputMode="decimal" /></div>
);

export function AssumptionsPanel({ studentMode, onChange }) {
  const [open, setOpen] = useState(false);
  const [a, setA] = useState({
    price: 25, customers: 400, growth: 3,
    cogs_pct: 35, payroll: 4500, rent: 1800, utilities: 400, marketing: 600, insurance: 250, other: 350,
    equipment: 40000, inventory: 8000, deposits: 6000, licenses: 2000, working: 15000,
    owner: 20000, loan: 60000, rate: 9, term: 7,
  });
  const set = (k, v) => setA((p) => ({ ...p, [k]: v }));

  const m = useMemo(() => {
    const price = N(a.price), cust = N(a.customers);
    const cogs = N(a.cogs_pct) / 100;
    const rev = price * cust;                       // monthly revenue
    const varCost = rev * cogs;                     // variable (COGS)
    const fixed = N(a.payroll) + N(a.rent) + N(a.utilities) + N(a.marketing) + N(a.insurance) + N(a.other);
    const cm = price * (1 - cogs);                  // contribution margin per sale
    const beUnits = cm > 0 ? fixed / cm : 0;
    const beRev = beUnits * price;
    const netMonthly = rev - varCost - fixed;
    // Funding / debt service
    const startup = N(a.equipment) + N(a.inventory) + N(a.deposits) + N(a.licenses) + N(a.working);
    const owner = N(a.owner), loan = N(a.loan), r = N(a.rate) / 100 / 12, n = N(a.term) * 12;
    const monthlyDebt = (loan > 0 && n > 0) ? (r > 0 ? loan * r / (1 - Math.pow(1 + r, -n)) : loan / n) : 0;
    const annualDebt = monthlyDebt * 12;
    // 36-month simulation (single source for Year 1-3 projection + Year-1 cash flow)
    const g = N(a.growth) / 100;
    const monthsRev = []; for (let i = 0; i < 36; i++) monthsRev.push(rev * Math.pow(1 + g, i));
    const yearFixed = fixed * 12;
    const yr = [0, 1, 2].map((y) => {
      const rr = monthsRev.slice(y * 12, y * 12 + 12).reduce((s, x) => s + x, 0);
      const vv = rr * cogs;
      return { rev: rr, exp: vv + yearFixed, profit: rr - vv - yearFixed };
    });
    const y1rev = yr[0].rev, y1net = yr[0].profit;
    const dscr = annualDebt > 0 ? y1net / annualDebt : null;
    const grossMargin = rev > 0 ? (rev - varCost) / rev : 0;
    const netMargin = y1rev > 0 ? y1net / y1rev : 0;
    const cashflow = [];
    for (let i = 0; i < 12; i++) { const rr = monthsRev[i]; cashflow.push([i + 1, Math.round(rr), Math.round(rr * cogs + fixed + monthlyDebt)]); }
    // Serializable financials — the single source of truth passed into the PDF + one-pagers.
    const financials = {
      projection: yr.map((y, i) => [`Year ${i + 1}`, Math.round(y.rev), Math.round(y.exp), Math.round(y.profit)]),
      breakeven: { fixed: Math.round(fixed), price, var: Math.round(price * cogs), units: beUnits, revenue: beRev },
      cashflow,
      summary: {
        monthly_revenue: Math.round(rev), monthly_profit: Math.round(netMonthly),
        startup: Math.round(startup), funded: Math.round(owner + loan), owner: Math.round(owner), loan: Math.round(loan),
        monthly_debt: Math.round(monthlyDebt), annual_debt: Math.round(annualDebt),
        dscr, gross_margin: grossMargin, net_margin: netMargin,
        year1_revenue: Math.round(y1rev), year1_profit: Math.round(y1net),
        be_units: Math.round(beUnits), be_revenue: Math.round(beRev), funding_ask: Math.round(loan),
      },
    };
    return { rev, fixed, cm, beUnits, beRev, netMonthly, y1rev, y1net, startup, monthlyDebt, dscr, grossMargin, netMargin, funded: owner + loan, financials };
  }, [a]);

  useEffect(() => { if (onChange) onChange(a, m.financials); }, [m]); // eslint-disable-line react-hooks/exhaustive-deps

  const ratios = [
    ["Gross Margin", pct(m.grossMargin), "The share of each sale left after direct costs — higher means more to cover overhead and profit."],
    ["Net Profit Margin", pct(m.netMargin), "The share of revenue kept as profit after all costs in Year 1."],
    ["Break-Even", `${Math.round(m.beUnits).toLocaleString()} sales/mo = ${$(m.beRev)}`, "The sales level where you stop losing money and start covering costs."],
    ["DSCR", m.dscr == null ? "—" : m.dscr.toFixed(2), m.dscr == null ? "Add a loan to compute debt-service coverage." : `The business projects $${m.dscr.toFixed(2)} of profit for every $1.00 of loan payments. Lenders often look for 1.15+.`],
  ];

  return (
    <div className="bg-navy-800 border border-white/10" data-testid="assumptions-panel">
      <button onClick={() => setOpen((o) => !o)} data-testid="asm-toggle" className="w-full flex items-center gap-2 p-4 text-left">
        <Calculator size={17} className="text-gold" />
        <span className="text-white text-sm font-medium">Financial Assumptions & Ratios</span>
        <span className="text-gray-500 text-xs">— the single financial source for your plan (updates the on-screen numbers, PDF & one-pagers)</span>
        <ChevronDown size={16} className={`ml-auto text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="p-4 pt-0 space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            {F("Avg sale / price", "price", a.price, set, "$")}
            {F("Customers / month", "customers", a.customers, set)}
            {F("Monthly growth", "growth", a.growth, set, "%")}
          </div>
          <div className="text-[11px] uppercase tracking-wide text-gray-500">Monthly costs</div>
          <div className="grid md:grid-cols-4 gap-3">
            {F("COGS", "cogs_pct", a.cogs_pct, set, "%")}
            {F("Payroll", "payroll", a.payroll, set, "$")}
            {F("Rent", "rent", a.rent, set, "$")}
            {F("Utilities", "utilities", a.utilities, set, "$")}
            {F("Marketing", "marketing", a.marketing, set, "$")}
            {F("Insurance", "insurance", a.insurance, set, "$")}
            {F("Other", "other", a.other, set, "$")}
          </div>
          <div className="text-[11px] uppercase tracking-wide text-gray-500">Startup & funding</div>
          <div className="grid md:grid-cols-4 gap-3">
            {F("Equipment", "equipment", a.equipment, set, "$")}
            {F("Inventory", "inventory", a.inventory, set, "$")}
            {F("Deposits", "deposits", a.deposits, set, "$")}
            {F("Licenses", "licenses", a.licenses, set, "$")}
            {F("Working capital", "working", a.working, set, "$")}
            {F("Owner investment", "owner", a.owner, set, "$")}
            {F("Loan amount", "loan", a.loan, set, "$")}
            {F("Interest rate", "rate", a.rate, set, "%")}
            {F("Loan term", "term", a.term, set, "yrs")}
          </div>

          {/* Live results */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="asm-results">
            {[["Monthly revenue", $(m.rev)], ["Break-even / mo", $(m.beRev)], ["Year-1 revenue", $(m.y1rev)], ["Year-1 profit", $(m.y1net)],
              ["Startup needed", $(m.startup)], ["Funded", $(m.funded)], ["Loan payment / mo", $(m.monthlyDebt)], ["Monthly profit", $(m.netMonthly)]].map(([k, v]) => (
              <div key={k} className={`border p-2.5 ${String(v).startsWith("-$") ? "border-red-500/40" : "border-white/10"}`}>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">{k}</div>
                <div className={`text-sm font-semibold ${String(v).startsWith("-$") ? "text-red-300" : "text-white"}`}>{v}</div>
              </div>
            ))}
          </div>

          {/* Ratio snapshot */}
          <div className="border border-gold/25 bg-gold/5 p-3" data-testid="ratio-snapshot">
            <div className="text-xs uppercase tracking-wide text-gold mb-2">Ratio Snapshot — what a loan officer scans first</div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {ratios.map(([label, val, why]) => (
                <div key={label} data-testid={`ratio-${label.replace(/\W+/g, "-").toLowerCase()}`}>
                  <div className="flex items-baseline justify-between">
                    <span className="text-gray-300 text-sm">{label}</span>
                    <span className="text-white font-semibold text-sm">{val}</span>
                  </div>
                  {studentMode && <div className="text-[11px] text-gray-400 mt-0.5 flex gap-1"><GraduationCap size={12} className="text-gold shrink-0 mt-0.5" /> {why}</div>}
                </div>
              ))}
            </div>
            <div className="text-[10px] text-gray-500 mt-2">Projections based on your assumptions — not a guarantee of loan approval. Lenders and loan programs apply different standards.</div>
          </div>
        </div>
      )}
    </div>
  );
}
